import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Building2, CalendarDays, ChevronRight, CircleCheck, CreditCard, Landmark, LifeBuoy,
  Package, RefreshCw, Unlink, UserPlus, Users, Video,
} from "lucide-react";
import { ErrorNotice, useRemote } from "./Controls";
import { useSharedToday } from "./Counts";
import { Segmented } from "components/ios/Effects";
import { RollingNumber, Sparkline } from "components/ios/Numbers";

// Tiles rise in once per page load, not on every return to Today.
let introPlayed = false;

const money = (minor = 0, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: minor % 100 ? 2 : 0 }).format(minor / 100);
const shortRef = (o) => `MAG-${String(o.id || "").slice(0, 8).toUpperCase()}`;
export function age(at, now) {
  const m = Math.max(0, Math.round((now - at) / 60000));
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} d`;
}
const dateParts = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : {
    day: d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase(),
    num: d.getDate(),
    time: d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }),
  };
};

// Most urgent first; each group is already oldest first from the server.
function queue(groups) {
  const rows = [];
  const add = (group, area, make) => (groups[group]?.items || []).forEach((item) => rows.push({ area, ...make(item) }));
  add("paymentReview", "orders", (o) => ({
    key: `r-${o.id}`, to: `/admin/orders/${o.id}`, icon: AlertTriangle, tint: "red", tone: "bad",
    title: `${/dispute/.test(o.reviewReason || "") ? "Dispute opened" : /refund/.test(o.reviewReason || "") ? "Refund recorded" : "Payment review"} — ${shortRef(o)}`,
    sub: [o.customerName, o.item].filter(Boolean).join(" · "), status: "Problem", at: o.at,
  }));
  add("awaitingTransfer", "orders", (o) => ({
    key: `t-${o.id}`, to: `/admin/orders/${o.id}`, icon: Landmark, tint: "gold", tone: "needs",
    title: `Match bank transfer — ${shortRef(o)}`, sub: [o.customerName, money(o.totalMinor, o.currency)].filter(Boolean).join(" · "),
    status: "Needs you", at: o.createdAt,
  }));
  add("needsPreparation", "orders", (o) => ({
    key: `p-${o.id}`, to: `/admin/orders/${o.id}`, icon: Package, tint: "blue", tone: "needs",
    title: `Prepare and release — ${shortRef(o)}`, sub: [o.customerName, o.item].filter(Boolean).join(" · "), status: "Needs you", at: o.at,
  }));
  add("openSupport", "support", (t) => ({
    key: `s-${t.id}`, to: `/admin/support/${t.id}`, icon: LifeBuoy, tint: "teal", tone: "needs",
    title: t.subject, sub: t.name || "Customer support", status: "Open", at: t.at,
  }));
  add("newLeads", "leads", (l) => ({
    key: `l-${l.id}`, to: `/admin/submissions/${l.id}`, icon: l.kind === "company" ? Building2 : UserPlus, tint: "blue", tone: "needs",
    title: `${l.kind === "company" ? "New company review" : l.kind === "resource" ? "Resource download" : l.kind === "webinar" ? "Webinar registration" : "New enquiry"} — ${l.name || "Unnamed"}`,
    sub: l.company || "Website form", status: "New", at: l.at,
  }));
  add("newApplications", "leads", (a) => ({
    key: `a-${a.id}`, to: `/admin/applications/${a.id}`, icon: Users, tint: "purple", tone: "needs",
    title: `New application — ${a.name || "Unnamed"}`, sub: a.role || "Careers", status: "New", at: a.at,
  }));
  if (groups.syncFailed?.count)
    rows.push({ area: "orders", key: "sync", to: "/admin/sync", icon: RefreshCw, tint: "gray", tone: "bad",
      title: `CarrierFlow: ${groups.syncFailed.capped ? "100+" : groups.syncFailed.count} message${groups.syncFailed.count === 1 ? "" : "s"} failed`,
      sub: "Open TMS sync to retry", status: "Problem", at: null });
  return rows;
}

const FILTERS = [
  { value: "all", label: "Needs you" },
  { value: "orders", label: "Orders" },
  { value: "leads", label: "Leads" },
  { value: "support", label: "Support" },
];

export default function Today() {
  const [tick, setTick] = useState(0), [filter, setFilter] = useState("all");
  const [intro] = useState(() => !introPlayed);
  useEffect(() => { introPlayed = true; }, []);
  // The shell already polls /admin/today for the menu counts; read it alone only without that feed.
  const shared = useSharedToday();
  const own = useRemote(shared ? null : "/admin/today", tick);
  const state = shared || own;
  const reload = () => (shared ? shared.refresh() : setTick(tick + 1));
  const data = state.data, now = data?.checkedAt || Date.now();
  const rows = data ? queue(data.groups) : [];
  const shown = filter === "all" ? rows : rows.filter((r) => r.area === filter);
  const n = data?.numbers || {}, link = n.link, next = data?.groups.upcoming?.items?.[0];
  const counts = { all: rows.length, orders: rows.filter((r) => r.area === "orders").length, leads: rows.filter((r) => r.area === "leads").length, support: rows.filter((r) => r.area === "support").length };
  return (
    <div className="aw-today">
      <ErrorNotice error={state.error} />
      {link?.state === "down" && (
        <div className="ios-banner ios-card ios-sheen aw-today-banner" role="status">
          <span className="ios-tile" data-tint="red"><Unlink aria-hidden="true" /></span>
          <span className="ios-row-main">
            <span className="ios-row-title">CarrierFlow link is down{link.downSince ? ` since ${new Date(link.downSince).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}</span>
            <span className="ios-row-sub">{link.lastErrorCode ? `Last error ${link.lastErrorCode}. ` : ""}The website keeps working: customers can still buy, pay and download.</span>
          </span>
          <Link className="ios-btn ios-btn-tinted" to="/admin/sync">View</Link>
        </div>
      )}
      {state.loading && !data ? (
        <div className={`ios-widgets aw-today-widgets ${intro ? "ios-intro" : ""}`} aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="ios-widget ios-card aw-today-skeleton" style={{ "--i": i }}>
              <span className="ios-skeleton" style={{ width: 36, height: 36, borderRadius: 10 }} />
              <span className="ios-skeleton" style={{ width: "58%", height: 14, marginTop: 18 }} />
              <span className="ios-skeleton" style={{ width: 76, height: 30, marginTop: 10 }} />
              <span className="ios-skeleton" style={{ width: "100%", height: 30, marginTop: 12 }} />
            </div>
          ))}
        </div>
      ) : (
      <div className={`ios-widgets aw-today-widgets ${intro ? "ios-intro" : ""}`}>
        {n.leadsLast7Days && (
          <Link className="ios-widget ios-card ios-sheen" to="/admin/submissions" style={{ "--i": 0 }}>
            <span className="ios-widget-top"><span className="ios-tile" data-tint="blue"><UserPlus aria-hidden="true" /></span><span className="ios-caption">Last 7 days</span></span>
            <p className="ios-widget-label">New leads</p>
            <p className="ios-widget-value"><RollingNumber value={n.leadsLast7Days.count} /></p>
            <span className="ios-widget-note">{n.leadsOpen ? `${n.leadsOpen.capped ? "60+" : n.leadsOpen.count} still new` : ""}</span>
            <Sparkline values={n.leadsLast7Days.days} label="New leads per day, last 7 days" />
          </Link>
        )}
        {n.paidLast7Days && (
          <Link className="ios-widget ios-card ios-sheen" to="/admin/orders" style={{ "--i": 1 }}>
            <span className="ios-widget-top"><span className="ios-tile" data-tint="green"><CreditCard aria-hidden="true" /></span><span className="ios-caption">Last 7 days</span></span>
            <p className="ios-widget-label">Paid orders</p>
            <p className="ios-widget-value"><RollingNumber value={n.paidLast7Days.count} /></p>
            <span className="ios-widget-note">{money(n.paidLast7Days.totalMinor)} received</span>
            <Sparkline values={n.paidLast7Days.days} label="Paid orders per day, last 7 days" />
          </Link>
        )}
        {n.awaitingTransfer && (
          <Link className="ios-widget ios-card ios-sheen" to="/admin/orders" style={{ "--i": 2 }}>
            <span className="ios-widget-top"><span className="ios-tile" data-tint="gold"><Landmark aria-hidden="true" /></span><span className="ios-caption">Matched by hand</span></span>
            <p className="ios-widget-label">Waiting for bank transfer</p>
            <p className="ios-widget-value"><RollingNumber value={n.awaitingTransfer.count} /></p>
            <span className="ios-widget-note">{money(n.awaitingTransfer.totalMinor)} expected</span>
          </Link>
        )}
        {next && (
          <Link className="ios-widget ios-card ios-sheen" to={`/admin/course-library/${next.id}`} style={{ "--i": 3 }}>
            <span className="ios-widget-top"><span className="ios-tile" data-tint="purple"><Video aria-hidden="true" /></span><span className="ios-caption">{dateParts(next.startAt)?.time}</span></span>
            <p className="ios-widget-label">Next live session</p>
            <p className="aw-today-next">{next.title}</p>
          </Link>
        )}
      </div>
      )}
      <div className="aw-today-grid">
        <section aria-labelledby="aw-needs">
          <div className="aw-today-controls">
            <Segmented label="Show" items={FILTERS.map((f) => ({ ...f, count: counts[f.value] || null }))} value={filter} onChange={setFilter} />
            <button className="ios-btn ios-btn-plain" onClick={reload}>
              <RefreshCw size={15} aria-hidden="true" /> {data ? `Updated ${new Date(now).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "Refresh"}
            </button>
          </div>
          <h2 className="ios-group-header" id="aw-needs"><span>Needs you</span><span>Most urgent first</span></h2>
          {state.loading && !data ? (
            <p className="ios-group-footer" role="status">Loading…</p>
          ) : shown.length ? (
            <ul className={`ios-group ${intro ? "ios-intro" : ""}`}>
              {shown.map((r, i) => (
                <li key={r.key} style={{ "--i": i + 2 }}>
                  <Link className="ios-row" to={r.to}>
                    <span className="ios-tile" data-tint={r.tint}><r.icon aria-hidden="true" /></span>
                    <span className="ios-row-main"><span className="ios-row-title">{r.title}</span><span className="ios-row-sub">{r.sub}</span></span>
                    <span className="ios-capsule" data-tone={r.tone}>{r.status}</span>
                    {r.at ? <span className="ios-row-meta ios-number">{age(r.at, now)}</span> : null}
                    <ChevronRight size={16} className="ios-chevron" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="ios-group aw-today-clear">
              <div className="ios-row"><span className="ios-tile" data-tint="green"><CircleCheck aria-hidden="true" /></span>
                <span className="ios-row-main"><span className="ios-row-title">All clear</span><span className="ios-row-sub">Nothing here needs you right now.</span></span></div>
            </div>
          )}
        </section>
        {data?.groups.upcoming && (
          <aside className="ios-card aw-today-upcoming" aria-labelledby="aw-upcoming">
            <div className="aw-today-upcoming-head"><h2 id="aw-upcoming">Coming up</h2><Link className="aw-link" to="/admin/course-library">See all</Link></div>
            {data.groups.upcoming.items.length ? (
              <ul>
                {data.groups.upcoming.items.map((c) => {
                  const d = dateParts(c.startAt);
                  return (
                    <li key={c.id}>
                      <span className="aw-date-chip"><small>{d?.day}</small>{d?.num}</span>
                      <span><Link to={`/admin/course-library/${c.id}`} className="aw-today-up-title">{c.title}</Link>
                        <span className="ios-footnote">{[d?.time, c.format === "live" ? "Live session" : c.category].filter(Boolean).join(" · ")}</span></span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="ios-footnote"><CalendarDays size={15} aria-hidden="true" /> No live sessions scheduled.</p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
